import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { Permissions } from 'src/utils/constants';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard, ModuleGuard } from 'src/auth/guard';
import { PermissionRoute } from 'src/auth/guard/permission-route';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/home/overview'));

const SchedulePage = lazy(() => import('src/modules/schedule/pages/overview'));

const DocumentPage = lazy(() => import('src/modules/documents/pages/overview'));

const HotelPage = lazy(() => import('src/modules/hotel/pages/overview'));
const HotelCreatePage = lazy(() => import('src/modules/hotel/pages/new'));
const HotelEditPage = lazy(() => import('src/modules/hotel/pages/edit'));

const RoomPage = lazy(() => import('src/modules/room/pages/overview'));
const RoomCreatePage = lazy(() => import('src/modules/room/pages/new'));
const RoomEditPage = lazy(() => import('src/modules/room/pages/edit'));

const CompetitionPage = lazy(() => import('src/modules/competition/pages/overview'));
const CompetitionCreatePage = lazy(() => import('src/modules/competition/pages/new'));
const CompetitionEditPage = lazy(() => import('src/modules/competition/pages/edit'));
const CompetitionViewPage = lazy(() => import('src/modules/competition/pages/view'));

const UserPage = lazy(() => import('src/modules/user/pages/overview'));
const UserCreatePage = lazy(() => import('src/modules/user/pages/new'));
const UserEditPage = lazy(() => import('src/modules/user/pages/edit'));

const FairPage = lazy(() => import('src/modules/fair/pages/overview'));
const FairCreatePage = lazy(() => import('src/modules/fair/pages/new'));
const FairEditPage = lazy(() => import('src/modules/fair/pages/edit'));

const OrganizationPage = lazy(() => import('src/modules/organization/pages/overview'));
const OrganizationCreatePage = lazy(() => import('src/modules/organization/pages/new'));
const OrganizationEditPage = lazy(() => import('src/modules/organization/pages/edit'));

const ProjectPage = lazy(() => import('src/modules/project/pages/overview'));
const ProjectEditPage = lazy(() => import('src/modules/project/pages/edit'));
const ProjectCreatePage = lazy(() => import('src/modules/project/pages/new'));
const ProjectDetailPage = lazy(() => import('src/modules/project/pages/detail'));
const ProjectConsentPage = lazy(() => import('src/modules/project/pages/consent'));

const ParticipantPage = lazy(() => import('src/modules/participant/pages/overview'));
const ParticipantCreatePage = lazy(() => import('src/modules/participant/pages/new'));
const ParticipantEditPage = lazy(() => import('src/modules/participant/pages/edit'));

const ParticipantBadgesPage = lazy(() => import('src/modules/participant-badges/pages/overview'));
const ConsentFormPage = lazy(() => import('src/modules/consent-form/pages/overview'));
const ProjectAwardsPage = lazy(
  () => import('src/modules/project-awards/pages/project-awards-page')
);
const ParticipantCertificatesPage = lazy(
  () => import('src/modules/participant-certificates/pages/overview')
);

const InvitationLetterPage = lazy(() => import('src/modules/invitation-letter/pages/overview'));
const VipPage = lazy(() => import('src/modules/vip/pages/overview'));
const VipCreatePage = lazy(() => import('src/modules/vip/pages/new'));
const VipEditPage = lazy(() => import('src/modules/vip/pages/edit'));

const FinalistPage = lazy(() => import('src/modules/finalist/pages/overview'));
const FinalistEditPage = lazy(() => import('src/modules/finalist/pages/edit'));

// Tour and Tour Reservation imports
const TourPage = lazy(() => import('src/modules/tour/pages/overview'));
const TourCreatePage = lazy(() => import('src/modules/tour/pages/new'));
const TourEditPage = lazy(() => import('src/modules/tour/pages/edit'));
const TourDetailsPage = lazy(() => import('src/modules/tour/pages/detail'));

const TourReservationPage = lazy(() => import('src/modules/tour-reservation/pages/overview'));
const TourReservationCreatePage = lazy(() => import('src/modules/tour-reservation/pages/new'));
// const TourReservationEditPage = lazy(() => import('src/modules/tour-reservation/pages/edit'));
// const TourReservationDetailsPage = lazy(() => import('src/modules/tour-reservation/pages/details'));

const TripPage = lazy(() => import('src/pages/dashboard/trip/overview'));
const JudgingSessionPage = lazy(() => import('src/modules/judging-session/pages/overview'));
const JudgingSessionCreatePage = lazy(() => import('src/modules/judging-session/pages/new'));
const JudgingSessionEditPage = lazy(() => import('src/modules/judging-session/pages/edit'));
const JudgingSessionAssignJuryPage = lazy(
  () => import('src/modules/judging-session/pages/assign-jury')
);
const JudgingSessionAssignProjectPage = lazy(
  () => import('src/modules/judging-session/pages/assign-project')
);
const JudgementEvaluationPage = lazy(
  () => import('src/modules/judgement-evaluation/pages/overview')
);
const JudgementEvaluationDetailPage = lazy(
  () => import('src/modules/judgement-evaluation/pages/detail')
);
const EvaluationOverviewPage = lazy(
  () => import('src/modules/evaluation-overview/views/evaluation-overview-view')
);
const RankingPage = lazy(() => import('src/modules/ranking/pages/overview'));

const JuryPage = lazy(() => import('src/modules/jury/pages/overview'));

// Import from our travel module instead of the old page
const TravelPage = lazy(() => import('src/modules/travel/pages/overview'));
const TravelEditPage = lazy(() => import('src/modules/travel/pages/edit'));

const PaymentPage = lazy(() => import('src/modules/payment/pages/overview'));
const PaymentSuccessPage = lazy(() => import('src/modules/payment/pages/success'));
const PaymentFailedPage = lazy(() => import('src/modules/payment/pages/failed'));

const PerformancePage = lazy(() => import('src/modules/performance/pages/overview'));

const ProfilePage = lazy(() => import('src/modules/profile/pages/overview'));
const ProfileEditPage = lazy(() => import('src/modules/profile/pages/edit'));

const AcademicYearPage = lazy(
  () => import('src/modules/define/pages/define-system/academic_year/overview')
);
const SemesterPage = lazy(() => import('src/modules/define/pages/define-system/semester/overview'));
const ShirtSizePage = lazy(
  () => import('src/modules/define/pages/define-system/shirt_size/overview')
);
const GradePage = lazy(() => import('src/modules/define/pages/define-system/grade/overview'));
const GenderPage = lazy(() => import('src/modules/define/pages/define-system/gender/overview'));
const MealPreferencePage = lazy(
  () => import('src/modules/define/pages/define-system/meal_preference/overview')
);
const FeedbackTypePage = lazy(
  () => import('src/modules/define/pages/define-system/feedback_type/overview')
);
const CompetitionTypePage = lazy(
  () => import('src/modules/define/pages/define-system/competition_type/overview')
);
const LanguagePage = lazy(() => import('src/modules/define/pages/define-system/language/overview'));
const ApplicationTypePage = lazy(
  () => import('src/modules/define/pages/competition/application_type/overview')
);
const PerformanceTypePage = lazy(
  () => import('src/modules/define/pages/competition/performance_type/overview')
);
const VenuePage = lazy(() => import('src/modules/define/pages/competition/venue/overview'));
const ProjectParticipationTypePage = lazy(
  () => import('src/modules/define/pages/competition/project_participation_type/overview')
);
const AwardTypePage = lazy(
  () => import('src/modules/define/pages/competition/project_award_type/overview')
);
const ConsentTermPage = lazy(
  () => import('src/modules/define/pages/competition/consent_term/overview')
);

const CompetitionPaymentTypePage = lazy(
  () => import('src/modules/define/pages/competition/competition_payment_type/overview')
);
const AgeIntervalPage = lazy(
  () => import('src/modules/define/pages/define-system/age_interval/overview')
);
const ContinentPage = lazy(() => import('src/modules/define/pages/geography/continent/overview'));
const OrganizationTypePage = lazy(
  () => import('src/modules/super-define/pages/organization/organization_type/overview')
);
const OrganizationSubTypePage = lazy(
  () => import('src/modules/super-define/pages/organization/organization_sub_type/overview')
);
const AddressTypePage = lazy(
  () => import('src/modules/define/pages/define-system/address_type/overview')
);
const CertificateTypePage = lazy(
  () => import('src/modules/define/pages/define-system/certificate_type/overview')
);
const DocumentFormatTypePage = lazy(
  () => import('src/modules/define/pages/define-system/document_format_type/overview')
);
const DocumentTypePage = lazy(
  () => import('src/modules/define/pages/define-system/document_type/overview')
);
const EmailTypePage = lazy(
  () => import('src/modules/define/pages/define-system/email_type/overview')
);
const PhoneTypePage = lazy(
  () => import('src/modules/define/pages/define-system/phone_type/overview')
);
const RolePage = lazy(() => import('src/modules/define/pages/define-system/role/overview'));
const VisaTypePage = lazy(
  () => import('src/modules/define/pages/define-system/visa_type/overview')
);
const DisabilityTypePage = lazy(
  () => import('src/modules/define/pages/define-system/disability_type/overview')
);
const TransportationTypePage = lazy(
  () => import('src/modules/define/pages/define-system/transportation_type/overview')
);
const UnitPage = lazy(
  () => import('src/modules/define/pages/company-insight/unit-overview/overview')
);
const CountryPage = lazy(() => import('src/modules/define/pages/geography/country/overview'));
const RegionPage = lazy(() => import('src/modules/define/pages/geography/region/overview'));
const ProvincePage = lazy(() => import('src/modules/define/pages/geography/province/overview'));
const DistrictPage = lazy(() => import('src/modules/define/pages/geography/district/overview'));
const CategoryPage = lazy(() => import('src/modules/define/pages/discipline/category/overview'));
const SubjectPage = lazy(() => import('src/modules/define/pages/discipline/subject/overview'));
const JudgementPeriodPage = lazy(
  () => import('src/modules/define/pages/judgement/Judgement_period/overview')
);
const JudgementCriterionPage = lazy(
  () => import('src/modules/define/pages/judgement/Judgement_criterion/overview')
);
const JudgementCriterionCategoryPage = lazy(
  () => import('src/modules/define/pages/judgement/judgement_criterion_category/overview')
);

const DepartmentPage = lazy(
  () => import('src/modules/define/pages/company-insight/department/overview')
);
const OfficePage = lazy(() => import('src/modules/define/pages/company-insight/office/overview'));
const GroupPage = lazy(() => import('src/modules/define/pages/company-insight/group/overview'));
const ProjectFileTypePage = lazy(
  () => import('src/modules/define/pages/define-system/project_file_type/overview')
);
const ModulePage = lazy(() => import('src/modules/define/pages/permission/module/overview'));
const ModulePermissionPage = lazy(
  () => import('src/modules/define/pages/permission/module_permission/overview')
);
const PageGuidelinePage = lazy(
  () => import('src/modules/define/pages/permission/page_guideline/overview')
);
const PermissionPage = lazy(
  () => import('src/modules/define/pages/permission/permission/overview')
);
const GroupRolePage = lazy(
  () => import('src/modules/define/pages/authorization/groupRole/overview')
);
const GroupRoleOrganizationPage = lazy(
  () => import('src/modules/define/pages/authorization/groupRoleOrganization/overview')
);
const GroupRoleOrganizationModulePage = lazy(
  () => import('src/modules/define/pages/authorization/groupRoleOrganizationModule/overview')
);
const RoleModulePermissionPage = lazy(
  () => import('src/modules/define/pages/authorization/roleModulePermission/overview')
);
const RoleOverviewPage = lazy(() => import('src/modules/define/pages/authorization/role/overview'));

// Accommodation
const RoomOccupantRolePage = lazy(
  () => import('src/modules/define/pages/accommodation/room_occupant_role/overview')
);
const RoomTypePage = lazy(
  () => import('src/modules/define/pages/accommodation/room_type/overview')
);

// Travel pages
const TerminalPage = lazy(() => import('src/modules/define/pages/travel/terminal/overview'));
const TransportLocationPage = lazy(
  () => import('src/modules/define/pages/travel/transport_location/overview')
);
const TransportCompanyPage = lazy(
  () => import('src/modules/define/pages/travel/transport_company/overview')
);
// Schedule Event imports
const ScheduleEventPage = lazy(
  () => import('src/modules/define/pages/schedule/schedule_event/overview')
);
const ScheduleEventCategoryPage = lazy(
  () => import('src/modules/define/pages/schedule/schedule_event_category/overview')
);

const DocumentCreatePage = lazy(() => import('src/modules/documents/pages/new'));

const LoggerPage = lazy(() => import('src/modules/logger/pages/overview'));
const FeedbackPage = lazy(() => import('src/modules/feedback/pages/overview'));

// ----------------------------------------------------------------------

const layoutContent = (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes = [
  {
    path: '/', // Dashboard content is now at root path
    element: CONFIG.auth.skip ? (
      <>{layoutContent}</>
    ) : (
      <AuthGuard>
        <ModuleGuard>{layoutContent}</ModuleGuard>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'schedule',
        children: [{ index: true, element: <SchedulePage /> }],
      },
      {
        path: 'documents',
        children: [
          { index: true, element: <DocumentPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <DocumentCreatePage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'accommodation',
        children: [
          {
            path: 'hotel',
            children: [
              { index: true, element: <HotelPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <HotelCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <HotelEditPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
          {
            path: 'room',
            children: [
              { index: true, element: <RoomPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <RoomCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <RoomEditPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'profile',
        children: [
          { index: true, element: <ProfilePage /> },
          { path: ':id', element: <ProfilePage /> },
          { path: 'edit/:id', element: <ProfileEditPage /> },
        ],
      },
      {
        path: 'fair',
        children: [
          { index: true, element: <FairPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <FairCreatePage />
              </PermissionRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <PermissionRoute permission={Permissions.UPDATE}>
                <FairEditPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'organization',
        children: [
          { index: true, element: <OrganizationPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <OrganizationCreatePage />
              </PermissionRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <PermissionRoute permission={Permissions.UPDATE}>
                <OrganizationEditPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'competition',
        children: [
          { index: true, element: <CompetitionPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <CompetitionCreatePage />
              </PermissionRoute>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionRoute permission={Permissions.VIEW}>
                <CompetitionViewPage />
              </PermissionRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <PermissionRoute permission={Permissions.UPDATE}>
                <CompetitionEditPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'user',
        children: [
          { index: true, element: <UserPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <UserCreatePage />
              </PermissionRoute>
            ),
          },
          // Edit route is intentionally removed to prevent any access to user edit page, even via direct URL.
        ],
      },
      {
        path: 'system',
        children: [
          {
            path: 'logger',
            children: [{ index: true, element: <LoggerPage /> }],
          },
          {
            path: 'feedback',
            children: [{ index: true, element: <FeedbackPage /> }],
          },
        ],
      },
      {
        path: 'projects',
        children: [
          { index: true, element: <ProjectPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <ProjectCreatePage />
              </PermissionRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <PermissionRoute permission={Permissions.UPDATE}>
                <ProjectEditPage />
              </PermissionRoute>
            ),
          },
          { path: 'detail/:id', element: <ProjectDetailPage /> },
          { path: 'consent/:id', element: <ProjectConsentPage /> },
        ],
      },
      // {
      //   path: 'performance', children: [
      //     { index: true, element: <PerformancePage /> },
      //   ]
      // },
      {
        path: 'participation',
        children: [
          {
            path: 'participant',
            children: [
              { index: true, element: <ParticipantPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <ParticipantCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <ParticipantEditPage />
                  </PermissionRoute>
                ),
              },
            ],
          },

          {
            path: 'finalist',
            children: [
              { index: true, element: <FinalistPage /> },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <FinalistEditPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
          {
            path: 'vip',
            children: [
              { index: true, element: <VipPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <VipCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <VipEditPage />
                  </PermissionRoute>
                ),
              },
            ],
          },
          { path: 'invitation-letter', element: <InvitationLetterPage /> },
        ],
      },
      {
        path: 'judgement',
        children: [
          {
            path: 'judging-session',
            children: [
              { index: true, element: <JudgingSessionPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <JudgingSessionCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <JudgingSessionEditPage />
                  </PermissionRoute>
                ),
              },
              { path: 'assign-jury/:id', element: <JudgingSessionAssignJuryPage /> },
              { path: 'assign-project/:id', element: <JudgingSessionAssignProjectPage /> },
            ],
          },
          {
            path: 'jury',
            children: [{ index: true, element: <JuryPage /> }],
          },
          {
            path: 'judgement-evaluation',
            children: [
              { index: true, element: <JudgementEvaluationPage /> },
              { path: 'detail/:id', element: <JudgementEvaluationDetailPage /> },
            ],
          },
          {
            path: 'overview',
            children: [{ index: true, element: <EvaluationOverviewPage /> }],
          },
          {
            path: 'ranking',
            children: [{ index: true, element: <RankingPage /> }],
          },
        ],
      },
      {
        path: 'travel',
        children: [
          { index: true, element: <TravelPage /> },
          {
            path: 'new',
            element: (
              <PermissionRoute permission={Permissions.ADD}>
                <TravelPage />
              </PermissionRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <PermissionRoute permission={Permissions.UPDATE}>
                <TravelEditPage />
              </PermissionRoute>
            ),
          },
        ],
      },
      {
        path: 'performance',
        children: [
          { index: true, element: <PerformancePage /> },
          // { path: 'new', element: <PermissionRoute permission={Permissions.ADD}><PerformancePage /></PermissionRoute> },
          // { path: 'edit/:id', element: <PermissionRoute permission={Permissions.UPDATE}><PerformanceEditPage /></PermissionRoute> }
        ],
      },
      {
        path: 'trip',
        children: [
          {
            path: 'tour',
            children: [
              { index: true, element: <TourPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <TourCreatePage />
                  </PermissionRoute>
                ),
              },
              {
                path: 'edit/:id',
                element: (
                  <PermissionRoute permission={Permissions.UPDATE}>
                    <TourEditPage />
                  </PermissionRoute>
                ),
              },
              { path: ':id', element: <TourDetailsPage /> },
            ],
          },
          {
            path: 'tour-reservation',
            children: [
              { index: true, element: <TourReservationPage /> },
              {
                path: 'new',
                element: (
                  <PermissionRoute permission={Permissions.ADD}>
                    <TourReservationCreatePage />
                  </PermissionRoute>
                ),
              },
              // { path: 'edit/:id', element: <PermissionRoute permission={Permissions.UPDATE}><TourReservationEditPage /></PermissionRoute> },
              // { path: 'details/:id', element: <TourReservationDetailsPage /> },
            ],
          },
        ],
      },
      {
        path: 'payment',
        children: [
          { index: true, element: <PaymentPage /> },
          { path: 'success', element: <PaymentSuccessPage /> },
          { path: 'failed', element: <PaymentFailedPage /> },
        ],
      },
      // Printable routes
      {
        path: 'printable',
        children: [
          {
            path: 'participant-badge',
            children: [{ index: true, element: <ParticipantBadgesPage /> }],
          },
          {
            path: 'consent-form',
            children: [{ index: true, element: <ConsentFormPage /> }],
          },
        ],
      },
      // Certificate routes
      {
        path: 'certificate',
        children: [
          {
            path: 'participant-certificate',
            children: [{ index: true, element: <ParticipantCertificatesPage /> }],
          },
          {
            path: 'project-award',
            children: [{ index: true, element: <ProjectAwardsPage /> }],
          },
        ],
      },

      {
        path: 'define',
        children: [
          {
            path: 'system',
            children: [
              { path: 'academic-year', element: <AcademicYearPage /> },
              { path: 'semester', element: <SemesterPage /> },
              { path: 'shirt-size', element: <ShirtSizePage /> },
              { path: 'grade', element: <GradePage /> },
              { path: 'gender', element: <GenderPage /> },
              { path: 'meal-preference', element: <MealPreferencePage /> },
              { path: 'feedback-type', element: <FeedbackTypePage /> },
              { path: 'competition-type', element: <CompetitionTypePage /> },
              { path: 'language', element: <LanguagePage /> },
              { path: 'age-interval', element: <AgeIntervalPage /> },
              { path: 'address-type', element: <AddressTypePage /> },
              { path: 'certificate-type', element: <CertificateTypePage /> },
              { path: 'document-format-type', element: <DocumentFormatTypePage /> },
              { path: 'document-type', element: <DocumentTypePage /> },
              { path: 'email-type', element: <EmailTypePage /> },
              { path: 'phone-type', element: <PhoneTypePage /> },
              { path: 'role', element: <RolePage /> },
              { path: 'visa-type', element: <VisaTypePage /> },
              { path: 'disability-type', element: <DisabilityTypePage /> },
              { path: 'transportation-type', element: <TransportationTypePage /> },
              { path: 'project-file-type', element: <ProjectFileTypePage /> },
            ],
          },
          {
            path: 'geography',
            children: [
              { path: 'continent', element: <ContinentPage /> },
              { path: 'country', element: <CountryPage /> },
              { path: 'region', element: <RegionPage /> },
              { path: 'province', element: <ProvincePage /> },
              { path: 'district', element: <DistrictPage /> },
            ],
          },
          {
            path: 'competition',
            children: [
              { path: 'competition-payment-type', element: <CompetitionPaymentTypePage /> },
              { path: 'application-type', element: <ApplicationTypePage /> },
              { path: 'project-participation-type', element: <ProjectParticipationTypePage /> },
              { path: 'project-award-type', element: <AwardTypePage /> },
              { path: 'consent-term', element: <ConsentTermPage /> },

              { path: 'performance-type', element: <PerformanceTypePage /> },
              { path: 'venue', element: <VenuePage /> },
            ],
          },
          {
            path: 'judgement',
            children: [
              { path: 'judgement-period', element: <JudgementPeriodPage /> },
              { path: 'judgement-criterion', element: <JudgementCriterionPage /> },
              { path: 'judgement-criterion-category', element: <JudgementCriterionCategoryPage /> },
            ],
          },
          {
            path: 'discipline',
            children: [
              { path: 'category', element: <CategoryPage /> },
              { path: 'subject', element: <SubjectPage /> },
            ],
          },
          {
            path: 'company-insight',
            children: [
              { path: 'unit', element: <UnitPage /> },
              { path: 'department', element: <DepartmentPage /> },
              { path: 'office', element: <OfficePage /> },
              { path: 'group', element: <GroupPage /> },
            ],
          },
          {
            path: 'permission',
            children: [
              { path: 'module', element: <ModulePage /> },
              { path: 'module-permission', element: <ModulePermissionPage /> },
              { path: 'page-guideline', element: <PageGuidelinePage /> },
              { path: 'permission', element: <PermissionPage /> },
            ],
          },
          {
            path: 'authorization',
            children: [
              { path: 'group-role', element: <GroupRolePage /> },
              { path: 'group-role-organization', element: <GroupRoleOrganizationPage /> },
              {
                path: 'group-role-organization-module',
                element: <GroupRoleOrganizationModulePage />,
              },
              { path: 'role-module-permission', element: <RoleModulePermissionPage /> },
              { path: 'role', element: <RoleOverviewPage /> },
            ],
          },
          {
            path: 'accommodation',
            children: [
              { path: 'room-occupant-role', element: <RoomOccupantRolePage /> },
              { path: 'room-type', element: <RoomTypePage /> },
            ],
          },
          {
            path: 'travel',
            children: [
              { path: 'terminal', element: <TerminalPage /> },
              { path: 'transport-location', element: <TransportLocationPage /> },
              { path: 'transport-company', element: <TransportCompanyPage /> },
            ],
          },
          {
            path: 'schedule',
            children: [
              { path: 'schedule-event', element: <ScheduleEventPage /> },
              { path: 'schedule-event-category', element: <ScheduleEventCategoryPage /> },
            ],
          },
        ],
      },
      {
        path: 'super-define',
        children: [
          {
            path: 'organization',
            children: [
              { path: 'organization-type', element: <OrganizationTypePage /> },
              { path: 'organization-sub-type', element: <OrganizationSubTypePage /> },
            ],
          },
        ],
      },
    ],
  },
];
